import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AgencyPage } from "./pages/AgencyPage";
import { ProfilePage } from "./pages/ProfilePage";
import { ProjectPage } from "./pages/ProjectPage";
import { LegalPage } from "./pages/LegalPage";
import { ScrollToTop } from "./components/ScrollToTop";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { CookieConsent } from "./components/CookieConsent";

/**
 * A retired path that stays alive as a client-side redirect, keeping whatever
 * query or hash the visitor arrived with (e.g. /agency#contact, /profile#projects).
 *
 * The server (scripts/serve.mjs, from LEGACY_REDIRECTS in lib/siteMeta.js) turns
 * these into real 301s, which is what search engines and fresh page loads see.
 * This copy only covers navigation inside an already-loaded page.
 */
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * Everything below the router. Split out from <App> so it can be mounted under
 * two different routers: BrowserRouter in the browser (main.tsx) and
 * StaticRouter during the build-time prerender (entry-server.tsx).
 */
export function AppRoutes() {
  return (
    <>
      <GoogleAnalytics />
      <ScrollToTop />
      <Routes>
        {/* The studio is the front door: it owns "/" so the root domain's
            authority sits on the page we actually want ranked. */}
        <Route path="/" element={<AgencyPage />} />
        <Route path="/agency" element={<LegacyRedirect to="/" />} />
        <Route path="/olga" element={<ProfilePage />} />
        <Route path="/profile" element={<LegacyRedirect to="/olga" />} />
        <Route path="/projects/:slug" element={<ProjectPage />} />
        <Route path="/privacy" element={<LegalPage docId="privacy" />} />
        <Route path="/terms" element={<LegalPage docId="terms" />} />
        <Route path="/cookies" element={<LegalPage docId="cookies" />} />
        {/* Anything unknown lands on the front door rather than a blank screen.
            A cold request for an unknown URL never gets here — the server
            answers it with a real 404. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsent />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}
